/**
 * 商品画像ギャラリー。表示順を持つ単一のメディア一覧と選択中のIDで、すべての表示を制御する。
 * 公開API: getState()、select(id)、update({ hiddenIds, order, variantMode })。
 * 初期方式は replace-first。仕様は docs/architecture/product-media-gallery-api.md を参照。
 * 旧実装の media-gallery / slider-component には依存しない。
*/

if (!customElements.get('ygb-media-gallery')) {
  customElements.define('ygb-media-gallery', class extends HTMLElement {
    static openLists = new Set();
    static inheritedScrollLock = false;

    connectedCallback() {
      // テーマエディタやクイック追加でHTMLが後から挿入された場合にも対応する。
      queueMicrotask(() => {
        if (this.isConnected && !this.controller) this.connect();
      });
    }

    connect() {
      this.slides = this.querySelector('[data-gallery-slides]');
      this.thumbnails = this.querySelector('[data-gallery-thumbnails]');
      this.panel = this.querySelector('[data-gallery-list]');
      this.toggle = this.querySelector('[data-gallery-action="open-list"]');
      if (!this.slides || !this.thumbnails || !this.panel) return;

      if (!this.items) {
        const thumbnails = new Map(Array.from(this.thumbnails.children, node => [node.dataset.galleryThumbnail, node]));
        this.items = new Map(Array.from(this.slides.children, node => [node.dataset.galleryId, {
          id: node.dataset.galleryId, slide: node, thumbnail: thumbnails.get(node.dataset.galleryId),
        }]));
        this.excludedIds = new Set((this.dataset.excludedIds || '').split('|').filter(Boolean));
        this.hiddenIds = new Set();
        this.variantMediaIds = new Set(JSON.parse(this.dataset.variantMediaIds || '[]').map(String));
        this.variantMode = 'replace-first';
        this.order = [...this.items.keys()];
        // URLで明示されたvariantだけ、初期表示から先頭へ差し替える。
        this.replacementId = this.urlReplacementId();
        this.activeId = this.replacementId || this.order[0] || null;
      }
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.desktop = window.matchMedia('(min-width: 1080px)');
      this.addEventListener('click', event => this.onClick(event), { signal });
      this.addEventListener('keydown', event => this.onKeydown(event), { signal });
      this.slides.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
      this.slides.addEventListener('scrollend', () => this.syncScroll(), { signal });
      // ユーザーがスクロールを操作したら、APIによる移動先の固定を解除する。
      for (const type of ['pointerdown', 'wheel']) {
        this.slides.addEventListener(type, () => { this.scrollTargetId = null; }, { passive: true, signal });
      }
      this.desktop.addEventListener('change', () => {
        this.closeList(false);
        this.syncLayout();
      }, { signal });
      this.resizeObserver = new ResizeObserver(() => this.alignSlide());
      this.resizeObserver.observe(this.slides);
      this.applyCollection();
      this.syncLayout();
      this.emit('ready');
    }

    disconnectedCallback() {
      if (!this.controller) return;
      this.closeList(false);
      this.controller.abort();
      this.controller = null;
      this.resizeObserver.disconnect();
      clearTimeout(this.scrollTimer);
      cancelAnimationFrame(this.alignmentFrame);
      this.alignmentFrame = null;
      this.scrollTargetId = null;
    }

    get ready() {
      return Boolean(this.controller && this.visibleIds);
    }

    getState() {
      return {
        mediaIds: [...this.items.keys()],
        visibleIds: [...this.visibleIds],
        hiddenIds: [...this.hiddenIds],
        excludedIds: [...this.excludedIds],
        order: [...this.order],
        variantMediaIds: [...this.variantMediaIds],
        variantMode: this.variantMode,
        replacementId: this.replacementId,
        activeId: this.activeId,
        index: this.visibleIds.indexOf(this.activeId),
        total: this.visibleIds.length,
      };
    }

    /** 更新は一括で反映する。配列は指定項目の全体を置き換え、省略した項目は維持する。 */
    update(options = {}) {
      if (!this.ready) throw new Error('Wait for ygb:gallery:ready before updating the gallery');
      const { hiddenIds, order, variantMode } = this.parseUpdate(options);
      this.hiddenIds = new Set(hiddenIds);
      this.order = order;
      let preferredId = this.activeId;
      if (variantMode !== this.variantMode) {
        this.replacementId = null;
        // navigate から戻すときも、URLで明示されたvariantだけ先頭へ反映する。
        if (variantMode === 'replace-first') {
          const urlVariantMediaId = this.urlReplacementId();
          if (urlVariantMediaId) {
            this.replacementId = urlVariantMediaId;
            preferredId = urlVariantMediaId;
          }
        }
      }
      this.variantMode = variantMode;
      this.applyCollection(preferredId);
      this.emit('update');
      return this.getState();
    }

    // 検証と省略値の補完だけを行い、状態やDOMは変更しない。
    parseUpdate(options) {
      if (!options || typeof options !== 'object' || Array.isArray(options)) throw new TypeError('Expected gallery update object');
      if (Object.keys(options).some(key => !['hiddenIds', 'order', 'variantMode'].includes(key))) throw new TypeError('Unknown gallery update option');
      const validate = (ids, field) => {
        if (!Array.isArray(ids)) throw new TypeError(`${field} must be an array`);
        const normalized = ids.map(id => String(id));
        if (new Set(normalized).size !== normalized.length) throw new TypeError(`${field} contains duplicate IDs`);
        if (normalized.some(id => !this.items.has(id) && !this.excludedIds.has(id))) throw new RangeError(`${field} contains an unknown media ID`);
        return normalized;
      };
      // 状態やDOMを変更する前に、すべての引数を検証する。
      const hiddenIds = 'hiddenIds' in options ? validate(options.hiddenIds, 'hiddenIds') : [...this.hiddenIds];
      const requestedOrder = 'order' in options ? validate(options.order, 'order') : this.order;
      const variantMode = 'variantMode' in options ? options.variantMode : this.variantMode;
      if (!['navigate', 'replace-first'].includes(variantMode)) throw new TypeError('Unknown gallery variant mode');
      const order = [...requestedOrder.filter(id => this.items.has(id))];
      for (const id of this.items.keys()) if (!order.includes(id)) order.push(id);
      return { hiddenIds, order, variantMode };
    }

    isAvailable(id) {
      return this.items.has(id) && !this.hiddenIds.has(id) && !this.excludedIds.has(id);
    }

    // Liquidの data-url-variant-media-id。クイック追加など別ページのURLとは混同しない。
    urlReplacementId() {
      const id = this.dataset.urlVariantMediaId;
      return this.variantMediaIds.has(id) && this.isAvailable(id) ? id : null;
    }

    // 元の順序から表示対象とDOM配置順を算出する。状態やDOMは変更しない。
    resolveCollection() {
      const replacementId = this.replacementId && this.isAvailable(this.replacementId) ? this.replacementId : null;
      const availableIds = this.order.filter(id => this.isAvailable(id));
      let visibleIds = availableIds;
      if (this.variantMode === 'replace-first') {
        const nonVariantIds = availableIds.filter(id => !this.variantMediaIds.has(id));
        const leadId = this.dataset.leadMediaId || '';
        const leadIsVariant = leadId !== '' && this.variantMediaIds.has(leadId) && availableIds.includes(leadId);
        // 商品の1枚目がvariantの代表画像でも、他に紹介メディアがあるなら一覧に残す。
        const keepLead = leadIsVariant && nonVariantIds.length > 0;
        const baseIds = availableIds.filter(id => (keepLead && id === leadId) || !this.variantMediaIds.has(id));
        if (replacementId) {
          const rest = baseIds.filter(id => id !== replacementId);
          // 選択画像が元の1枚目そのものなら、2件目以降は落とさない。
          visibleIds = baseIds[0] === replacementId ? [replacementId, ...rest] : [replacementId, ...rest.slice(1)];
        } else if (baseIds.length === 0 && leadIsVariant && availableIds.length === 1) {
          // 画像が商品の1枚目だけなら、variant画像でも表示する。
          visibleIds = [leadId];
        } else {
          visibleIds = baseIds;
        }
      }
      // 非表示の要素も保持し、通常方式へ戻す際は元の順序で再利用する。
      const renderOrder = replacementId
        ? [replacementId, ...this.order.filter(id => id !== replacementId)]
        : this.order;
      return { replacementId, visibleIds, renderOrder };
    }

    // 表示一覧の反映から選択確定、空一覧の閉鎖、スクロール位置の同期までを行う。
    applyCollection(preferredId = this.activeId, reason = 'update') {
      const { replacementId, visibleIds, renderOrder } = this.resolveCollection();
      this.replacementId = replacementId;
      this.visibleIds = visibleIds;
      for (const [index, id] of renderOrder.entries()) {
        const item = this.items.get(id);
        const position = this.visibleIds.indexOf(id);
        const hidden = position === -1;
        item.slide.hidden = hidden;
        item.thumbnail.hidden = hidden;
        const button = item.thumbnail.querySelector('button');
        if (!hidden && button.dataset.galleryLabel) {
          button.setAttribute('aria-label', button.dataset.galleryLabel.replace('[index]', position + 1));
        }
        // 並べ替え時も既存のメディア要素とプレイヤーのインスタンスを維持する。
        if (this.slides.children[index] !== item.slide) this.slides.insertBefore(item.slide, this.slides.children[index] || null);
        if (this.thumbnails.children[index] !== item.thumbnail) this.thumbnails.insertBefore(item.thumbnail, this.thumbnails.children[index] || null);
      }
      this.hidden = this.visibleIds.length === 0;
      this.querySelector('[data-gallery-total]').textContent = this.visibleIds.length;
      this.querySelectorAll('[data-gallery-action="previous"], [data-gallery-action="next"]').forEach(button => {
        button.disabled = this.visibleIds.length < 2;
      });
      const nextId = this.visibleIds.includes(preferredId) ? preferredId : this.visibleIds[0] || null;
      this.commitSelection(nextId, reason);
      if (this.hidden) this.closeList(false);
      this.alignSlide();
    }

    /** 非表示・除外・未登録のメディアにはfalseを返す。非表示のメディアは再表示しない。 */
    select(id) {
      if (!this.ready) throw new Error('Wait for ygb:gallery:ready before selecting media');
      id = String(id);
      if (!this.visibleIds.includes(id)) return false;
      this.commitSelection(id, 'select');
      this.revealSelection();
      return true;
    }

    // 既存の VariantSelects.updateMedia() と連携するための変換処理。
    setActiveMedia(sectionMediaId) {
      if (!this.ready) throw new Error('Wait for ygb:gallery:ready before selecting media');
      const prefix = `${this.dataset.section}-`;
      const rawId = String(sectionMediaId);
      const id = rawId.startsWith(prefix) ? rawId.slice(prefix.length) : rawId;
      let selected;
      if (this.variantMode === 'replace-first' && this.variantMediaIds.has(id)) {
        selected = this.isAvailable(id);
        if (selected) {
          this.replacementId = id;
          this.applyCollection(id, 'variant');
          this.revealSelection();
        }
      } else {
        selected = this.select(id);
      }
      this.closeList();
      return selected;
    }

    commitSelection(id, reason) {
      const changed = this.activeId !== id;
      this.activeId = id;
      this.renderSelection();
      if (!changed) return;
      window.pauseAllMedia?.();
      const deferred = this.items.get(id)?.slide.querySelector('.deferred-media');
      if (deferred && !deferred.hasAttribute('loaded')) {
        const load = () => {
          if (this.isConnected && this.activeId === id && !deferred.hasAttribute('loaded')) deferred.loadContent(false);
        };
        // セクションやクイック追加の挿入時は、この要素より後に product-model.js が読み込まれる場合がある。
        if (typeof deferred.loadContent === 'function') load();
        else customElements.whenDefined(deferred.localName).then(load);
      }
      document.querySelector('sticky-header')?.dispatchEvent(new Event('preventHeaderReveal'));
      const index = this.visibleIds.indexOf(id) + 1;
      this.querySelector('[data-gallery-status]').textContent = id
        ? (window.accessibilityStrings?.imageAvailable || 'Image [index] is available').replace('[index]', index)
        : '';
      this.emit('change', reason);
    }

    renderSelection() {
      for (const { id, slide, thumbnail } of this.items.values()) {
        const active = id === this.activeId;
        slide.classList.toggle('is-active', active);
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
        const button = thumbnail.querySelector('button');
        if (active) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      }
      this.querySelector('[data-gallery-current]').textContent = this.visibleIds.indexOf(this.activeId) + 1;
    }

    move(step) {
      if (!this.visibleIds.length) return;
      const index = this.visibleIds.indexOf(this.activeId);
      this.select(this.visibleIds[(index + step + this.visibleIds.length) % this.visibleIds.length]);
    }

    alignSlide(behavior = 'instant') {
      cancelAnimationFrame(this.alignmentFrame);
      this.alignmentFrame = null;
      this.scrollTargetId = null;
      if (!this.controller || this.desktop.matches || this.hidden) return;
      this.alignmentFrame = requestAnimationFrame(() => {
        this.alignmentFrame = null;
        if (!this.controller || this.desktop.matches) return;
        const slide = this.items.get(this.activeId)?.slide;
        if (slide) {
          // 以前のスクロール完了通知で、新しく指定した選択画像が上書きされるのを防ぐ。
          if (behavior === 'smooth' && Math.abs(this.slides.scrollLeft - slide.offsetLeft) > 1) {
            this.scrollTargetId = this.activeId;
          }
          this.slides.scrollTo({ left: slide.offsetLeft, behavior });
        }
      });
    }

    onScroll() {
      clearTimeout(this.scrollTimer);
      // scrollend非対応ブラウザ向けの代替処理。スワイプ途中のスライドを選択しないようにする。
      this.scrollTimer = setTimeout(() => this.syncScroll(), 100);
    }

    syncScroll() {
      if (!this.controller || this.desktop.matches || this.alignmentFrame || !this.visibleIds.length) return;
      const target = this.items.get(this.scrollTargetId)?.slide;
      if (target && Math.abs(this.slides.scrollLeft - target.offsetLeft) > 1) return;
      this.scrollTargetId = null;
      clearTimeout(this.scrollTimer);
      const nearest = this.visibleIds.reduce((best, id) => {
        const distance = Math.abs(this.items.get(id).slide.offsetLeft - this.slides.scrollLeft);
        return !best || distance < best.distance ? { id, distance } : best;
      }, null);
      this.commitSelection(nearest.id, 'scroll');
    }

    // 選択操作後の移動とサムネイルの追従を共通化する。
    revealSelection() {
      this.alignSlide(window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth');
      this.revealThumbnail();
    }

    revealThumbnail() {
      if (!this.desktop.matches) return;
      const item = this.items.get(this.activeId)?.thumbnail;
      if (!item) return;
      const scroller = this.panel.querySelector('.ygb-gallery__list-scroll');
      const bounds = scroller.getBoundingClientRect();
      const rect = item.getBoundingClientRect();
      if (rect.top < bounds.top) scroller.scrollTop += rect.top - bounds.top;
      else if (rect.bottom > bounds.bottom) scroller.scrollTop += rect.bottom - bounds.bottom;
    }

    syncLayout() {
      this.panel.inert = !this.desktop.matches && !this.listOpen;
      this.alignSlide();
      this.revealThumbnail();
    }

    openList() {
      if (this.desktop.matches || this.hidden || this.listOpen) return;
      this.listOpen = true;
      this.returnFocus = document.activeElement;
      const owner = this.constructor;
      if (!owner.openLists.size) owner.inheritedScrollLock = document.body.classList.contains('overflow-hidden');
      owner.openLists.add(this);
      document.body.classList.add('overflow-hidden');
      this.panel.inert = false;
      this.panel.classList.add('is-open');
      this.panel.setAttribute('role', 'dialog');
      this.panel.setAttribute('aria-modal', 'true');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.panel.querySelector('[data-gallery-action="close-list"]').focus({ preventScroll: true });
    }

    closeList(restoreFocus = true) {
      if (!this.listOpen) return;
      this.listOpen = false;
      this.panel.classList.remove('is-open');
      this.panel.removeAttribute('role');
      this.panel.removeAttribute('aria-modal');
      this.toggle.setAttribute('aria-expanded', 'false');
      const owner = this.constructor;
      owner.openLists.delete(this);
      if (!owner.openLists.size && !owner.inheritedScrollLock && !document.querySelector('dialog[open], modal-dialog[open], product-modal[open]')) {
        document.body.classList.remove('overflow-hidden');
      }
      if (restoreFocus && this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      this.panel.inert = !this.desktop.matches;
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.dataset.gallerySelect) {
        this.select(button.dataset.gallerySelect);
        this.closeList();
        return;
      }
      switch (button.dataset.galleryAction) {
        case 'previous': this.move(-1); break;
        case 'next': this.move(1); break;
        case 'open-list': this.openList(); break;
        case 'close-list': this.closeList(); break;
      }
    }

    onKeydown(event) {
      if (!this.listOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.closeList();
      } else if (event.key === 'Tab') {
        const buttons = [...this.panel.querySelectorAll('button')].filter(button => !button.closest('[hidden]'));
        const first = buttons[0];
        const last = buttons[buttons.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    emit(name, reason) {
      this.dispatchEvent(new CustomEvent(`ygb:gallery:${name}`, {
        bubbles: true, detail: { ...this.getState(), reason },
      }));
    }
  });
}
